import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import { handleCardClickEvent } from '../../../../../helpers/cards';
import { getImagePath, commifyNumber } from '../../../../../helpers/common';
import { handleClickTheFavorite } from '../../../../../helpers/favorites';
import ImageWrapper from '../../../../../new-components/ImageWrapper';
import { EntityTypes } from '../../../../../new-lib/constants/common';
import { CardClickEventData } from '../../../../../new-lib/types/common';
import FavoriteIcon from '../../../../../new-components/svg-icons/FavoriteIcon';
import ImageAltDescription from '../../../../../helpers/images-alt';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'universal-cookie';
import CardCompareIcon from '../../../../compare/components/CardCompareIcon';
import CardTags from '../../../../../new-components/cards/CardTags';

interface CardProps {
  property: any;
  cardPosition?: number;
}
const PropertyCard = ({ property, cardPosition }: CardProps) => {
  const { t } = useTranslation(['common', 'entity']);
  const { asPath, locale } = useRouter();
  const favorites = useSelector(
    (state: any) => state.favorites.propertyFavorites
  );
  const [isFavorite, setIsFavorite] = useState<boolean>(
    favorites.includes(property.id)
  );
  const dispatch = useDispatch();
  const clickEventData: CardClickEventData = {
    eventAction: 'prop_click',
    recommended: property.recommended ?? false,
    index: cardPosition,
    property: property,
    compound: property.compound,
    area: property.area,
    featureName: 'property',
    eventLocation: 'explore_properties'
  };
  const handleClickFavorite = (e: MouseEvent): void => {
    const payload = {
      client_id: new Cookies().get('clientID', { doNotParse: true }),
      entity_id: property.id
    };
    e.preventDefault();
    handleClickTheFavorite(isFavorite, EntityTypes.COMPOUND, dispatch, payload);
    setIsFavorite(!isFavorite);
  };

  const imageAltDescription = new ImageAltDescription(
    property,
    'property',
    locale
  );
  const altDescription = imageAltDescription.getAltDescription();
  return (
    <Link
      href={`/compound/[compound_slug]/property/[property_slug]`}
      as={`/compound/${property.compound?.slug}/property/${
        property?.slug || property?.id
      }`}
      prefetch={false}
      style={{ width: 'calc(50% - 16px)', borderRadius: '18px' }}
    >
      <Container onClick={() => handleCardClickEvent(clickEventData)}>
        <div className="card-cover">
          <ImageWrapper
            objectFit="cover"
            src={getImagePath(property.image)}
            alt={altDescription}
            fill
            withSkeleton
            imageSize="medium"
            quality={50}
            sizes="50vw"
          />

          {(property.is_launch_summary ||
            (property.tags && property.tags.length > 0)) && (
            <CardTags
              tags={[
                ...(property.tags as Array<string>),
                ...(property.is_launch_summary ? ['Launch'] : [])
              ]}
            />
          )}

          <div className="card-actions">
            <CardCompareIcon
              entity={property}
              entityType={EntityTypes.PROPERTY}
            />

            <div
              className="fav-icon"
              onClick={(e: any) => handleClickFavorite(e)}
            >
              <FavoriteIcon
                stroke={isFavorite ? 'none' : '#fff'}
                fill={isFavorite ? 'url(#favoriteGradient)' : 'transparent'}
              />
            </div>
          </div>
        </div>
        <div className="card-details">
          <span className="property-type">{property.property_type.name}</span>

          {property.min_price ? (
            <h4 className="price">
              {`${commifyNumber(property.min_price)} ${
                property.currency || 'EGP'
              }`}
            </h4>
          ) : (
            <span className="price">{t('price_tba', { ns: 'entity' })}</span>
          )}

          <div className="property-details">
            {property.number_of_bedrooms ? (
              <span>{`${property.number_of_bedrooms} ${t('beds')}`}</span>
            ) : null}
            {property.number_of_bathrooms ? (
              <span>{`${property.number_of_bathrooms} ${t('baths')}`}</span>
            ) : null}
            {property.min_unit_area ? (
              <span>{`${property.min_unit_area} ${t('m²')}`}</span>
            ) : null}
            {!property.number_of_bedrooms &&
              !property.number_of_bathrooms &&
              !property.min_unit_area && (
                <h4>{t('details_tba', { ns: 'entity' })}</h4>
              )}
          </div>
        </div>

        <svg width="0" height="0" style={{ position: 'absolute' }}>
          <defs>
            <linearGradient
              id="favoriteGradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop
                offset="0%"
                style={{ stopColor: '#F84F4F', stopOpacity: 1 }}
              />
              <stop
                offset="100%"
                style={{ stopColor: '#DA3333', stopOpacity: 1 }}
              />
            </linearGradient>
          </defs>
        </svg>
      </Container>
    </Link>
  );
};

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #fff;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0px 1px 9px 0px rgba(188, 188, 188, 0.16);

  .card-cover {
    width: 100%;
    height: 150px;
    position: relative;
    overflow: hidden;
    position: relative;

    .card-actions {
      display: flex;
      flex-direction: row;
      gap: 12px;
      position: absolute;
      top: 14px;
      inset-inline-end: 14px;

      .icon {
        width: 34px;
        height: 34px;
        border-radius: 200px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .fav-icon {
        width: 34px;
        height: 34px;
        border-radius: 200px;
        background: rgba(44, 49, 56, 0.7);
        display: flex;
        align-items: center;
        justify-content: center;
        svg {
          width: 22px;
          height: 22px;
        }
      }
    }
  }

  .card-details {
    display: flex;
    flex-direction: column;
    padding: 16px;
    .property-type {
      color: #6f6f6f;
      font-family: materialLight;
      font-size: 12px;
      line-height: 130%;
      text-transform: capitalize;
    }

    .price {
      color: #212427;
      font-family: materialSemiBold;
      font-size: 18px;
      line-height: normal;
      margin: 4px 0 8px;
    }

    .property-details {
      display: flex;
      align-items: center;
      font-family: materialRegular;
      font-size: 14px;
      color: #212427;

      span {
        padding-inline-end: 5px;
        line-height: normal;
      }

      span:not(:first-child) {
        padding-inline-start: 5px;
        border-inline-start: 1px solid #c4c4c4;
      }
    }
  }
`;

export default PropertyCard;
